/**
 * AudioExporter — offline render of the full audio mix.
 *
 * Uses Tone.Offline() to render all tracks, clips, effects, fades,
 * volume, and pan into a single AudioBuffer. Exports to WAV format.
 */
import * as Tone from 'tone';
import { useEditorStore } from '../store/editorStore';
import { getProxy } from './proxyManager';
import { getFadeGainAtTime } from '../lib/fadeUtils';
import { createToneEffect, hasMixAPI, ensureCrossfaderWorklet } from './effectsManager';
import type { EffectClip } from '../types';

/**
 * Render the full audio mix offline. Returns a stereo AudioBuffer.
 */
export async function renderAudioMix(originalDataMap?: Map<string, ArrayBuffer>): Promise<AudioBuffer> {
  const { project } = useEditorStore.getState();
  const durationSec = project.durationMs / 1000;
  if (durationSec <= 0) throw new Error('No project duration');

  // Decode all audio buffers — prefer originals when available
  const audioCtx = new AudioContext();
  const bufferMap = new Map<string, AudioBuffer>();

  for (const asset of project.assets) {
    if (bufferMap.has(asset.id)) continue;
    const rawData = originalDataMap?.get(asset.id) ?? await getProxy(asset.id);
    if (!rawData) continue;
    try {
      const buf = await audioCtx.decodeAudioData(rawData.slice(0));
      bufferMap.set(asset.id, buf);
    } catch { /* non-audio asset */ }
  }
  await audioCtx.close();

  // Offline render
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rendered: AudioBuffer = await (Tone.Offline as any)(async ({ transport }: { transport: { start: (t: number) => void } }) => {
    // Register crossfader worklet in the offline context
    await ensureCrossfaderWorklet();

    const masterGain = new Tone.Gain(1);
    const masterOutput: Tone.ToneAudioNode = masterGain;

    // Apply master effects chain
    const masterEffects = project.masterEffects ?? [];
    const activeMasterEffects = masterEffects.filter((e) => !e.bypassed);
    if (activeMasterEffects.length > 0) {
      const toneEffects = [];
      for (const effect of activeMasterEffects) {
        toneEffects.push(await createToneEffect(effect.type, effect.params));
      }
      // Chain: masterGain → effect1 → effect2 → ... → destination
      masterGain.connect(toneEffects[0]);
      for (let i = 0; i < toneEffects.length - 1; i++) {
        toneEffects[i].connect(toneEffects[i + 1]);
      }
      toneEffects[toneEffects.length - 1].toDestination();
    } else {
      masterGain.toDestination();
    }

    for (const track of project.tracks) {
      if (track.type !== 'audio' || track.muted) continue;

      const channel = new Tone.Channel(
        track.volume <= 0 ? -Infinity : 20 * Math.log10(track.volume ?? 1),
        track.pan ?? 0,
      );

      // Gather all effect clips targeting this audio track
      const effectClipsForTrack: EffectClip[] = [];
      for (const et of project.tracks) {
        if (et.type === 'effect' && et.parentTrackId === track.id && et.effectClips?.length) {
          effectClipsForTrack.push(...et.effectClips.filter((ec) => !ec.bypassed));
        }
      }

      // Build signal chain: channel → [effectClips...] → [trackEffects...] → masterGain
      const nodes: Tone.ToneAudioNode[] = [];

      // Effect clip nodes — schedule mix automation
      // Uses unified mix API: single source of truth for crossfade math
      for (const ec of effectClipsForTrack) {
        const toneEffect = await createToneEffect(ec.effectType, ec.params);
        const mixValue = ec.params.wet ?? 0.3;
        const startSec = ec.startMs / 1000;
        const endSec = (ec.startMs + ec.durationMs) / 1000;
        const rampTime = 0.005;

        if (hasMixAPI(toneEffect)) {
          // Unified mix scheduling — crossfade math lives in CompositeReverb/worklet
          toneEffect.setMix(0);
          toneEffect.setMixAtTime(0, Math.max(0, startSec - rampTime));
          toneEffect.rampMixToTime(mixValue, startSec);
          toneEffect.setMixAtTime(mixValue, Math.max(startSec, endSec - rampTime));
          toneEffect.rampMixToTime(0, endSec);
        } else if ('wet' in toneEffect) {
          const wet = (toneEffect as { wet: Tone.Signal<'normalRange'> }).wet;
          wet.value = 0;
          wet.setValueAtTime(0, Math.max(0, startSec - rampTime));
          wet.linearRampToValueAtTime(mixValue, startSec);
          wet.setValueAtTime(mixValue, Math.max(startSec, endSec - rampTime));
          wet.linearRampToValueAtTime(0, endSec);
        }

        nodes.push(toneEffect);
      }

      // Track effects
      const trackEffects = (track.effects ?? []).filter((e) => !e.bypassed);
      for (const effect of trackEffects) {
        nodes.push(await createToneEffect(effect.type, effect.params));
      }

      // Wire chain
      if (nodes.length > 0) {
        channel.connect(nodes[0]);
        for (let i = 0; i < nodes.length - 1; i++) {
          nodes[i].connect(nodes[i + 1]);
        }
        nodes[nodes.length - 1].connect(masterGain);
      } else {
        channel.connect(masterGain);
      }

      for (const clip of track.clips) {
        const srcBuf = bufferMap.get(clip.assetId);
        if (!srcBuf) continue;

        const toneBuf = new Tone.ToneAudioBuffer(srcBuf);
        const player = new Tone.Player(toneBuf);

        // Apply fades via gain automation
        if (clip.fadeIn || clip.fadeOut) {
          const fadeGain = new Tone.Gain(1);
          player.connect(fadeGain);
          fadeGain.connect(channel);

          const startTimeSec = clip.startMs / 1000;

          if (clip.fadeIn) {
            const fadeDur = clip.fadeIn.durationMs / 1000;
            fadeGain.gain.setValueAtTime(0, startTimeSec);
            fadeGain.gain.linearRampToValueAtTime(1, startTimeSec + fadeDur);
          }

          if (clip.fadeOut) {
            const fadeOutStart = (clip.startMs + clip.durationMs - clip.fadeOut.durationMs) / 1000;
            const fadeOutEnd = (clip.startMs + clip.durationMs) / 1000;
            fadeGain.gain.setValueAtTime(1, fadeOutStart);
            fadeGain.gain.linearRampToValueAtTime(0, fadeOutEnd);
          }
        } else {
          player.connect(channel);
        }

        const offsetSec = clip.sourceOffsetMs / 1000;
        const durationSec = clip.durationMs / 1000;
        player.start(clip.startMs / 1000, offsetSec, durationSec);
      }
    }

    transport.start(0);
  }, durationSec, 2, 44100);

  return rendered;
}

/**
 * Encode an AudioBuffer to WAV format.
 */
export function encodeWav(buffer: AudioBuffer, bitDepth: 16 | 24 | 32 = 16): ArrayBuffer {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const numSamples = buffer.length;
  const dataSize = numSamples * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;

  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  // WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Interleave and write samples
  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) {
    channels.push(buffer.getChannelData(c));
  }

  let offset = headerSize;
  for (let i = 0; i < numSamples; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      if (bitDepth === 16) {
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      } else if (bitDepth === 24) {
        const val = sample < 0 ? sample * 0x800000 : sample * 0x7FFFFF;
        view.setUint8(offset, val & 0xFF);
        view.setUint8(offset + 1, (val >> 8) & 0xFF);
        view.setUint8(offset + 2, (val >> 16) & 0xFF);
      } else {
        view.setFloat32(offset, sample, true);
      }
      offset += bytesPerSample;
    }
  }

  return arrayBuffer;
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
